import { Badge, Card, CardContent, CardHeader, CardTitle } from "@/ui";
import { 
  Apple, 
  Activity, 
  BookOpen, 
  Smartphone, 
  Target, 
  TrendingUp,
  Heart,
  Users,
  Calendar
} from "lucide-react";
const patientsImage = "/assets/website/images/Patients_using_health_technology_ea50cdeb.png";
const clinicalMockup = "/assets/website/images/backgrounds/Clinical_medication_alert_app_white_background_3c02b9d5.png";
const wellnessMockup = "/assets/website/images/backgrounds/Wellness_tracking_app_white_background_58cf6c4e.png";

const patientSolutions = [
  {
    icon: Apple,
    title: "Wellness Tracking & Food Logging",
    description: "Comprehensive nutrition and lifestyle monitoring with MyFitnessPal integration",
    features: [
      "Automated food logging",
      "Nutritional insights",
      "Calorie tracking",
      "Dietary recommendations"
    ],
    integrations: "",
    impact: "Better dietary adherence"
  },
  {
    icon: Activity,
    title: "Exercise & Fitness Monitoring",
    description: "Personalized fitness tracking with healthcare provider oversight",
    features: [
      "Activity monitoring",
      "Exercise recommendations",
      "Progress tracking",
      "Provider notifications"
    ],
    integrations: "",
    impact: "Improved physical health"
  },
  {
    icon: BookOpen,
    title: "Health Education & Engagement",
    description: "Personalized health content and interactive wellness programs",
    features: [
      "Condition-specific education",
      "Interactive health modules",
      "Progress tracking",
      "Reminder notifications"
    ],
    integrations: "Provider EHR • Care Plans",
    impact: "Enhanced health literacy"
  }
];

const patientOutcomes = [
  {
    icon: Target,
    title: "Care Plan Adherence",
    description: "Patients stay on track with personalized reminders and tracking",
    improvement: "40% increase in adherence rates"
  },
  {
    icon: TrendingUp,
    title: "Health Outcomes",
    description: "Measurable improvements in chronic disease management",
    improvement: "25% better clinical indicators"
  },
  {
    icon: Heart,
    title: "Patient Satisfaction",
    description: "Higher engagement through user-friendly wellness tools",
    improvement: "90% patient satisfaction score"
  }
];

const engagementFeatures = [
  {
    icon: Smartphone,
    title: "Mobile-First Design",
    description: "Intuitive apps that patients actually want to use"
  },
  {
    icon: Users,
    title: "Family Integration",
    description: "Connect with caregivers and family members"
  },
  {
    icon: Calendar,
    title: "Smart Reminders",
    description: "Personalized notifications for medications and appointments"
  }
];

export default function SolutionsPatients() {

  return (
    <section id="patients" className="py-20">
      {/* Hero Image Section */}
      <div className="relative h-80 mb-12 overflow-hidden">
        <img 
          src={patientsImage} 
          alt="Patients Using Health Technology"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background/95 via-background/80 to-background/60" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center px-4 w-full max-w-6xl mx-auto">
            <Badge variant="secondary" className="mb-4" data-testid="badge-patients">
              Solutions for Patients
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4" data-testid="text-patients-title">
              Empowering Patient Wellness
            </h2>
            <p className="text-xl text-foreground max-w-3xl mx-auto" data-testid="text-patients-description">
              Comprehensive wellness tools that integrate seamlessly with healthcare providers, 
              helping patients take control of their health journey.
            </p>
          </div>
        </div>
      </div>

      {/* Mobile App Mockups Section */}
      <div className="container mx-auto mb-20">
        <div className="bg-card rounded-xl p-8 shadow-sm border border-card-border max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center justify-items-center">
            {/* Clinical Mobile Mockup */}
            <div className="text-center p-6 bg-background/50 rounded-lg border border-card-border hover:shadow-md transition-all duration-300">
              <img 
                src={clinicalMockup} 
                alt="Clinical Medication Alert Mobile App"
                className="w-56 md:w-64 lg:w-72 h-auto mx-auto hover:scale-105 transition-transform duration-300"
                data-testid="img-clinical-mockup"
              />
              <h3 className="text-lg font-semibold text-foreground mt-4" data-testid="text-clinical-mockup-title">
                Clinical Support
              </h3>
              <p className="text-sm text-muted-foreground mt-2" data-testid="text-clinical-mockup-description">
                Medication alerts & health monitoring
              </p>
            </div>
            
            {/* Wellness Mobile Mockup */}
            <div className="text-center p-6 bg-background/50 rounded-lg border border-card-border hover:shadow-md transition-all duration-300">
              <img 
                src={wellnessMockup} 
                alt="Wellness Tracking Mobile App"
                className="w-56 md:w-64 lg:w-72 h-auto mx-auto hover:scale-105 transition-transform duration-300"
                data-testid="img-wellness-mockup"
              />
              <h3 className="text-lg font-semibold text-foreground mt-4" data-testid="text-wellness-mockup-title">
                Wellness Tracking
              </h3>
              <p className="text-sm text-muted-foreground mt-2" data-testid="text-wellness-mockup-description">
                Fitness, nutrition & lifestyle monitoring
              </p>
            </div>
          </div>
        </div>
      </div>
      
      <div className="container mx-auto max-w-7xl">

        {/* Patient Solutions Grid */}
        <div className="grid lg:grid-cols-3 gap-8 mb-16 place-items-center justify-items-center">
          {patientSolutions.map((solution, index) => (
            <Card key={index} className="hover-elevate w-full max-w-md mx-auto shadow-sm border border-card-border" data-testid={`card-patient-solution-${index}`}>
              <CardHeader>
                <div className="flex items-center gap-3 mb-3">
                  <div className="flex items-center justify-center w-12 h-12 bg-accent/20 rounded-lg">
                    <solution.icon className="h-6 w-6 text-accent-foreground" />
                  </div>
                </div>
                <CardTitle className="text-xl" data-testid={`text-patient-solution-title-${index}`}>
                  {solution.title}
                </CardTitle>
                <p className="text-muted-foreground" data-testid={`text-patient-solution-description-${index}`}>
                  {solution.description}
                </p>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Features */}
                <div>
                  <h4 className="font-semibold mb-3 text-sm">Key Features</h4>
                  <ul className="space-y-2">
                    {solution.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center text-sm text-muted-foreground" data-testid={`text-patient-feature-${index}-${featureIndex}`}>
                        <div className="w-1.5 h-1.5 bg-accent rounded-full mr-3 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Integrations */}
                <div>
                  <h4 className="font-semibold mb-2 text-sm">Integrations</h4>
                  <Badge variant="outline" className="text-xs" data-testid={`badge-integrations-${index}`}>
                    {solution.integrations}
                  </Badge>
                </div>

                {/* Impact */}
                <div className="p-3 bg-accent/10 rounded">
                  <div className="text-sm font-medium text-accent-foreground" data-testid={`text-impact-${index}`}>
                    {solution.impact}
                  </div>
                </div>

              </CardContent>
            </Card>
          ))}
        </div>

        {/* Patient Outcomes */}
        <div className="bg-card rounded-lg p-8 mb-12 max-w-6xl mx-auto shadow-sm border border-card-border">
          <h3 className="text-2xl font-bold text-center mb-8" data-testid="text-outcomes-title">
            Patient Outcomes<sup className="text-red-500 text-xs">*</sup>
          </h3>
          <div className="grid md:grid-cols-3 gap-6 place-items-center justify-items-center">
            {patientOutcomes.map((outcome, index) => (
              <div key={index} className="text-center max-w-sm w-full mx-auto" data-testid={`outcome-${index}`}>
                <div className="flex items-center justify-center w-16 h-16 bg-accent/20 rounded-lg mx-auto mb-4">
                  <outcome.icon className="h-8 w-8 text-accent-foreground" />
                </div>
                <h4 className="font-semibold text-foreground mb-2" data-testid={`outcome-title-${index}`}>
                  {outcome.title}
                </h4>
                <p className="text-sm text-muted-foreground mb-3" data-testid={`outcome-description-${index}`}>
                  {outcome.description}
                </p>
                <Badge variant="secondary" className="text-xs" data-testid={`outcome-improvement-${index}`}>
                  {outcome.improvement}
                </Badge>
              </div>
            ))}
          </div>
        </div>

        {/* Engagement Features */}
        <div className="mb-12 max-w-6xl mx-auto">
          <h3 className="text-2xl font-bold text-center mb-8" data-testid="text-engagement-title">
            Patient Engagement Features
          </h3>
          <div className="grid md:grid-cols-3 gap-6 place-items-center justify-items-center">
            {engagementFeatures.map((feature, index) => (
              <Card key={index} className="text-center w-full max-w-sm mx-auto shadow-sm border border-card-border" data-testid={`engagement-feature-${index}`}>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg mx-auto mb-4">
                    <feature.icon className="h-6 w-6 text-primary" />
                  </div>
                  <h4 className="font-semibold text-foreground mb-2" data-testid={`engagement-title-${index}`}>
                    {feature.title}
                  </h4>
                  <p className="text-sm text-muted-foreground" data-testid={`engagement-description-${index}`}>
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
}